import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { submitLead } from "../lib/api";
import { CONTACT } from "../data/content";

const STEPS = [
  { key: "projet", label: "Votre projet" },
  { key: "logement", label: "Votre logement" },
  { key: "situation", label: "Votre situation" },
  { key: "contact", label: "Vos coordonnées" },
];

const PROJECTS = [
  { value: "Pompe à chaleur", desc: "Chauffage + eau chaude" },
  { value: "Climatisation", desc: "Chaud et froid" },
  { value: "Isolation", desc: "Combles, murs, planchers" },
  { value: "Rénovation globale", desc: "Plusieurs postes" },
];
const HEATINGS = ["Chaudière fioul", "Chaudière gaz", "Électrique", "Bois / pellets", "Autre"];
const HOME_TYPES = ["Maison individuelle", "Appartement"];
const OWNERSHIP = ["Propriétaire", "Locataire"];
const REVENUS = [
  { value: "tres_modeste", label: "Très modeste", desc: "< 22 000 € / an" },
  { value: "modeste", label: "Modeste", desc: "22 000 – 28 000 €" },
  { value: "intermediaire", label: "Intermédiaire", desc: "28 000 – 38 000 €" },
  { value: "superieur", label: "Supérieur", desc: "> 38 000 €" },
];

export default function EligibilitySimulator({ compact = false }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    projet: "",
    type_logement: "",
    surface: "",
    chauffage_actuel: "",
    proprietaire: "",
    revenus: "",
    nom: "",
    telephone: "",
    email: "",
    code_postal: "",
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const progress = ((step + 1) / STEPS.length) * 100;

  const canNext = () => {
    if (step === 0) return !!form.projet;
    if (step === 1) return !!form.type_logement && !!form.surface && !!form.chauffage_actuel;
    if (step === 2) return !!form.proprietaire && !!form.revenus;
    if (step === 3) return !!form.nom && !!form.telephone;
    return false;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        source: "simulator",
        nom: form.nom,
        telephone: form.telephone,
        email: form.email || undefined,
        code_postal: form.code_postal || undefined,
        type_logement: form.type_logement || undefined,
        surface: form.surface ? parseInt(form.surface, 10) : undefined,
        revenus: form.revenus || undefined,
        chauffage_actuel: form.chauffage_actuel || undefined,
        projet: form.projet || undefined,
        proprietaire: form.proprietaire || undefined,
      };
      const res = await submitLead(payload);
      setDone(res);
      toast.success("Estimation envoyée ! Nous vous recontactons sous 24h.");
    } catch (e) {
      toast.error("Erreur d'envoi. Merci de réessayer ou d'appeler le " + CONTACT.phoneDisplay);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="simulator-result" className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden">
        <div className="blob" style={{ background: "#7DD923", width: 300, height: 300, top: -100, right: -50, opacity: 0.2 }} />
        <div className="relative z-10 max-w-xl">
          <div className="w-14 h-14 rounded-full bg-[#7DD923] flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-black" />
          </div>
          <div className="eyebrow">Vos aides estimées</div>
          <h3 className="font-display uppercase text-white text-3xl md:text-5xl mt-3 tracking-tight leading-tight">
            {done.aides_estimees || "Estimation envoyée"}
          </h3>
          <p className="text-white/70 mt-4">
            Merci <strong>{done.nom}</strong>, un expert Optimeo Habitat vous recontacte sous <strong>24h</strong> pour préciser votre étude et monter le dossier d'aides.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href={CONTACT.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="simulator-rdv-btn"
              className="btn-primary"
            >
              Prendre RDV maintenant
            </a>
            <a href={`tel:${CONTACT.phoneLink}`} className="btn-secondary" data-testid="simulator-call-btn">Nous appeler</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="eligibility-simulator"
      className={`glass rounded-3xl p-6 md:p-10 relative overflow-hidden ${compact ? "" : "shadow-[0_30px_80px_-40px_rgba(125,217,35,0.25)]"}`}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="blob" style={{ background: "#7DD923", width: 260, height: 260, top: -100, right: -80, opacity: 0.14 }} />
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Simulateur d'éligibilité</div>
          <div className="text-xs text-white/40">Étape {step + 1} / {STEPS.length}</div>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-6">
          <div className="h-full bg-[#7DD923] transition-all" style={{ width: `${progress}%` }} />
        </div>

        <h3 className="font-display uppercase text-white text-2xl md:text-3xl tracking-tight">
          {STEPS[step].label}
        </h3>

        <div className="mt-6 space-y-4">
          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PROJECTS.map((p) => (
                <button
                  key={p.value}
                  data-testid={`sim-projet-${p.value.replace(/\s/g, "-").toLowerCase()}`}
                  onClick={() => set("projet", p.value)}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    form.projet === p.value
                      ? "border-[#7DD923] bg-[#7DD923]/10"
                      : "border-white/10 bg-white/5 hover:border-white/30"
                  }`}
                >
                  <div className="text-white font-semibold">{p.value}</div>
                  <div className="text-white/50 text-xs mt-1">{p.desc}</div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {HOME_TYPES.map((h) => (
                  <button
                    key={h}
                    data-testid={`sim-logement-${h.replace(/\s/g,"-").toLowerCase()}`}
                    onClick={() => set("type_logement", h)}
                    className={`p-4 rounded-xl border text-sm font-semibold transition-all ${
                      form.type_logement === h ? "border-[#7DD923] bg-[#7DD923]/10 text-white" : "border-white/10 bg-white/5 text-white/80 hover:border-white/30"
                    }`}
                  >{h}</button>
                ))}
              </div>
              <input
                data-testid="sim-surface-input"
                type="number"
                placeholder="Surface habitable (m²)"
                value={form.surface}
                onChange={(e) => set("surface", e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] focus:ring-1 focus:ring-[#7DD923] outline-none w-full"
              />
              <div>
                <div className="text-white/60 text-xs mb-2 uppercase tracking-wider">Chauffage actuel</div>
                <div className="flex flex-wrap gap-2">
                  {HEATINGS.map((h) => (
                    <button
                      key={h}
                      data-testid={`sim-chauffage-${h.replace(/\s|\//g,"-").toLowerCase()}`}
                      onClick={() => set("chauffage_actuel", h)}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${
                        form.chauffage_actuel === h ? "border-[#7DD923] bg-[#7DD923] text-black font-semibold" : "border-white/10 bg-white/5 text-white/80 hover:border-white/30"
                      }`}
                    >{h}</button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {OWNERSHIP.map((o) => (
                  <button
                    key={o}
                    data-testid={`sim-statut-${o.toLowerCase()}`}
                    onClick={() => set("proprietaire", o)}
                    className={`p-4 rounded-xl border text-sm font-semibold transition-all ${
                      form.proprietaire === o ? "border-[#7DD923] bg-[#7DD923]/10 text-white" : "border-white/10 bg-white/5 text-white/80 hover:border-white/30"
                    }`}
                  >{o}</button>
                ))}
              </div>
              <div>
                <div className="text-white/60 text-xs mb-2 uppercase tracking-wider">Vos revenus fiscaux annuels</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {REVENUS.map((r) => (
                    <button
                      key={r.value}
                      data-testid={`sim-revenus-${r.value}`}
                      onClick={() => set("revenus", r.value)}
                      className={`text-left p-3 rounded-xl border transition-all ${
                        form.revenus === r.value ? "border-[#7DD923] bg-[#7DD923]/10" : "border-white/10 bg-white/5 hover:border-white/30"
                      }`}
                    >
                      <div className="text-white text-sm font-semibold">{r.label}</div>
                      <div className="text-white/50 text-xs">{r.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="sim-nom" placeholder="Nom & prénom *" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none sm:col-span-2" />
              <input data-testid="sim-tel" placeholder="Téléphone *" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none" />
              <input data-testid="sim-cp" placeholder="Code postal" value={form.code_postal} onChange={(e) => set("code_postal", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none" />
              <input data-testid="sim-email" placeholder="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none sm:col-span-2" />
              <p className="sm:col-span-2 text-xs text-white/40 leading-relaxed">
                En envoyant ce formulaire, vous acceptez d'être recontacté par Optimeo Habitat pour votre projet de rénovation. Vos données restent confidentielles.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            data-testid="sim-prev-btn"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="text-white/60 hover:text-white text-sm flex items-center gap-2 disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          {step < STEPS.length - 1 ? (
            <button
              data-testid="sim-next-btn"
              disabled={!canNext()}
              onClick={() => setStep((s) => s + 1)}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continuer <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="sim-submit-btn"
              disabled={!canNext() || loading}
              onClick={submit}
              className="btn-primary disabled:opacity-40"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi</> : <>Voir mes aides <ArrowRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
